import {Outlet, useMatch} from '@github-ui/react-router'
import {pullRequestsLayoutRoute} from './layout-route'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useRespondToRouteChange} from '../hooks/use-respond-to-route-change'
import {type PropsWithChildren, useCallback, useRef} from 'react'
import {PageDataContextProvider} from '@github-ui/pull-request-page-data-tooling/page-data-context'
import {
  condensedResponsiveWrapperClasses,
  fullWidthResponsiveWrapperClasses,
} from '../components/StickyPullRequestHeader'
import {ChangesRoutePath, ChecksRoutePath, FilesRoutePath} from './route-paths'
import {DiffComparisonViewer} from '../components/DiffComparisonViewer'
import {UserNameDisplayContextProvider} from '@github-ui/profile-reference/UserNameDisplayContext'
import {LivePullRequestHeader} from '../components/PullRequestHeader'
import type {HeaderPageData} from '../page-data/payloads/header'
import {useRespondToAliveUpdate} from '../hooks/use-refetch-on-alive-update'

export function LayoutEntrypoint() {
  const {
    data: {pullRequest, bannersData, repository, urls, user, aliveChannel, pageTitle, stack, mergeStatusButtonData},
  } = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const {refetch: refetchTabCounts} = useRouteQuery(pullRequestsLayoutRoute, 'tabCounts')
  const {refetch: refetchDiffstat} = useRouteQuery(pullRequestsLayoutRoute, 'diffstat')
  const needUpdateRef = useRef(false)

  const markNeedsUpdate = useCallback(() => {
    needUpdateRef.current = true
  }, [])

  useRespondToAliveUpdate(aliveChannel, markNeedsUpdate, {git_updated: true, timeline_updated: true})

  // Refetch shared data when the route changes to keep diffstat and tab counts up to date
  // we only refetch on navigation to avoid excessive refetching.
  const handleRouteChange = useCallback(() => {
    if (!needUpdateRef.current) return

    needUpdateRef.current = false
    refetchTabCounts()
    refetchDiffstat()
  }, [refetchTabCounts, refetchDiffstat])

  useRespondToRouteChange(handleRouteChange)

  return (
    <LayoutComponent
      aliveChannel={aliveChannel}
      pullRequest={pullRequest}
      bannersData={bannersData}
      repository={repository}
      pageTitle={pageTitle}
      urls={urls}
      user={user}
      mergeStatusButtonData={mergeStatusButtonData}
      stack={stack}
    >
      <Outlet />
    </LayoutComponent>
  )
}

export function LayoutComponent({
  aliveChannel,
  pullRequest,
  bannersData,
  pageTitle,
  repository,
  stack,
  urls,
  user,
  children,
  mergeStatusButtonData,
}: PropsWithChildren<HeaderPageData>) {
  const isFilesRoute = useMatch(`${FilesRoutePath}/*`) !== null
  const isChangesRoute = useMatch(`${ChangesRoutePath}/*`) !== null
  const isChecksRoute = useMatch(`${ChecksRoutePath}/*`) !== null

  const isFilesOrChangesRoute = isFilesRoute || isChangesRoute

  const responsiveWrapperClasses =
    isFilesOrChangesRoute || isChecksRoute ? fullWidthResponsiveWrapperClasses : condensedResponsiveWrapperClasses

  return (
    <PageDataContextProvider basePageDataUrl={urls.conversation}>
      <UserNameDisplayContextProvider value={repository.userNameDisplayConfiguration || undefined}>
        <DiffComparisonViewer.Container className={`pr-mt-4 ${responsiveWrapperClasses}`}>
          <DiffComparisonViewer.Header divider="none" padding="none">
            <LivePullRequestHeader
              aliveChannel={aliveChannel}
              bannersData={bannersData}
              pageTitle={pageTitle}
              isFilesRoute={isFilesOrChangesRoute}
              repository={repository}
              pullRequest={pullRequest}
              stack={stack}
              urls={urls}
              user={user}
              mergeStatusButtonData={mergeStatusButtonData}
            />
          </DiffComparisonViewer.Header>
          {children}
        </DiffComparisonViewer.Container>
      </UserNameDisplayContextProvider>
    </PageDataContextProvider>
  )
}
